// TenantLabels.js
// TVET terminology as a tenant label pack (migration plan §7.1 / D8).
//
// The new UI speaks CampusCloud's vocabulary. The legacy 1TVET UI spoke TESDA's, and the words
// were not decoration: the same page had a different name on a TVET campus. The legacy menu is the
// evidence for every mapping below (`../1TVET/1TVET-UI/src/components/side-menu/SideMenuNew.js`):
//
//   /assessment/subjects           "Institutional Assessment"   :322 (staff), :387 (student)
//   /formativeassessment/subjects  "Self-check"                 :321 (staff), :385 (student)
//   /homeworksubjects              "Performance Task"           :323 (staff), :389 (student)
//   /subject                       "Unit of Competency"         :320 (staff), "Unit" :383 (student)
//
// Two deliberate omissions:
//
//   * `Subjects` / `Subject Groups` keep their CampusCloud names. The legacy admin menu did the
//     same (:353-354), so translating them would be a change, not parity.
//   * The microcredentials module keeps "Performance Task" for its capstone. TVET uses that phrase
//     for homework, but D2 keeps microcredentials switched off for TVET campuses, so the two never
//     appear side by side. If microcredentials is ever enabled for TVET, the capstone needs a
//     different word — this file is where that decision lands.
//
// Callers read labels at render time, never at module scope: `lms-subdomain` is written by init.js
// after the bundle has loaded, which is exactly why TenantAssets.js exposes functions rather than
// constants too.

import React from 'react';
import { isTvetTenant } from './TvetTenant';

// The CampusCloud wording, i.e. what the shared pages already render.
//
// The first four keys are the bare concepts. The keys after them are whole labels the merged pages
// already carry as complete strings (assessmentModuleConfig.js, config/learningModules.js,
// HomeworksPage/*). They are separate entries rather than `${label} Subjects` compositions because
// CampusCloud's own strings are not all compositions either ("Create Summative" drops the word
// "Assessment"), and because the reverse map this file builds needs the exact rendered string.
export const DEFAULT_LABELS = Object.freeze({
   formativeAssessment: 'Formative Assessment',
   summativeAssessment: 'Summative Assessment',
   homework: 'Homework & Project',
   lesson: 'Lesson',
   // Only ever rendered by the microcredentials module, which D2 keeps off for TVET. Present here
   // so the word has one home and the terminology gate can allow exactly one file.
   performanceTaskCapstone: 'Performance Task',
   // assessmentModuleConfig.js — the subject-list pages and the breadcrumbs above them.
   formativeAssessmentSubjects: 'Formative Assessment Subjects',
   formativeAssessments: 'Formative Assessments',
   createFormative: 'Create Formative',
   summativeAssessmentSubjects: 'Summative Assessment Subjects',
   summativeAssessments: 'Summative Assessments',
   createSummative: 'Create Summative',
   // config/learningModules.js — the subject/lesson pages.
   lessonPlural: 'Lessons',
   createLesson: 'Create Lesson',
   // HomeworksPage — the two label flavours it already distinguishes.
   createHomework: 'Create Homework',
   createHomeworkProject: 'Create Homework & Project',
   createNewHomeworkProject: 'Create new Homework & Project',
   homeworkSubjects: 'Homework Subjects',
   homeworkShort: 'Homework',
});

// Only the keys that differ. Every key must exist in DEFAULT_LABELS (asserted in
// TenantLabels.test.js) so a typo cannot silently add an unused label.
//
// The page-level values are the legacy strings, verbatim, from the 1TVET screens that rendered
// them: AssessmentSubjects.js:144 ("Institutional Assessment" breadcrumb) and :187 ("Create
// Institutional Assessment"), FormativeAssessmentSubjects.js:132/:169, HomeworkSubjects.js:219/:260,
// SubjectGroupings.js:2983/:3018 (legacy file of the same name in pages/SubjectPage).
export const TVET_LABELS = Object.freeze({
   formativeAssessment: 'Self-check',
   summativeAssessment: 'Institutional Assessment',
   homework: 'Performance Task',
   lesson: 'Unit of Competency',
   formativeAssessmentSubjects: 'Self-check',
   formativeAssessments: 'Self-checks',
   createFormative: 'Create Self-check',
   summativeAssessmentSubjects: 'Institutional Assessment',
   summativeAssessments: 'Institutional Assessments',
   createSummative: 'Create Institutional Assessment',
   lessonPlural: 'Units of Competency',
   createLesson: 'Create Unit of Competency',
   createHomework: 'Create Performance Task',
   createHomeworkProject: 'Create Performance Task',
   createNewHomeworkProject: 'Create new Performance Task',
   // The student landing page names the module three different ways; all three are the same term.
   homeworkSubjects: 'Performance Task Subjects',
   homeworkShort: 'Performance Task',
});

const MERGED = Object.freeze({ ...DEFAULT_LABELS, ...TVET_LABELS });

/**
 * Labels for the tenant this browser is serving. `subdomain` defaults to the stored
 * `lms-subdomain`, so components call it without arguments; tests pass a key explicitly.
 */
export const getTenantLabels = (subdomain) => (isTvetTenant(subdomain) ? MERGED : DEFAULT_LABELS);

/** Component-facing alias: same value, named so call sites read as hooks. */
export const useTenantLabels = (subdomain) => getTenantLabels(subdomain);

// CampusCloud wording -> this tenant's wording, built from the two dictionaries so the reverse
// mapping cannot drift from the forward one.
const buildReverseMap = () => {
   const reverse = {};

   Object.keys(TVET_LABELS).forEach((key) => {
      const from = DEFAULT_LABELS[key];
      const to = TVET_LABELS[key];

      if (from && to && from !== to && !reverse[from]) {
         reverse[from] = to;
      }
   });

   return reverse;
};

const TVET_REVERSE_MAP = buildReverseMap();

/**
 * Translates one already-rendered string. Exact matches only: `Formative Assessment Subjects`
 * is a different label from `Formative Assessment` and is left alone until a page needs it.
 */
export const translateLabelText = (text, subdomain) => {
   if (typeof text !== 'string' || !isTvetTenant(subdomain)) {
      return text;
   }

   return TVET_REVERSE_MAP[text.trim()] || text;
};

const translateNode = (node, subdomain) => {
   if (typeof node === 'string') {
      return translateLabelText(node, subdomain);
   }

   if (Array.isArray(node)) {
      const translated = node.map((child) => translateNode(child, subdomain));

      return translated.some((child, index) => child !== node[index]) ? translated : node;
   }

   if (!React.isValidElement(node) || node.props === undefined) {
      return node;
   }

   const { children } = node.props;

   if (children === undefined || children === null) {
      return node;
   }

   const translated = translateNode(children, subdomain);

   return translated === children ? node : React.cloneElement(node, undefined, translated);
};

/**
 * Rewrites menu labels for the tenant without touching menuConfig.js.
 *
 * menuConfig.js builds its entries once, at import time, as `<Link><span>Text</span></Link>`
 * elements; by the time a menu is rendered `lms-subdomain` is known, so the labels are translated
 * here — the same shape of hook as hideUnavailableMenuItems in TvetTenant.js, and for the same
 * reason: it keeps the pass in one place instead of a `labels.x` import in every builder.
 *
 * Returns the input array unchanged (same reference) for CampusCloud tenants, so nothing pays for
 * this on other deployments.
 */
export const applyTenantLabelsToMenu = (items, subdomain) => {
   if (!isTvetTenant(subdomain) || !Array.isArray(items)) {
      return items;
   }

   const translateItem = (item) => {
      if (!item || typeof item !== 'object') {
         return item;
      }

      const label = translateNode(item.label, subdomain);
      const children = Array.isArray(item.children)
         ? item.children.map(translateItem)
         : item.children;

      if (label === item.label && children === item.children) {
         return item;
      }

      return { ...item, label, children };
   };

   return items.map(translateItem);
};

// The label fields a page module exposes, across the two module configs the app has:
// config/learningModules.js (`label`, `pluralLabel`, `createLabel`) and
// AssessmentPage/common/assessmentModuleConfig.js (`displayName` and friends).
const MODULE_LABEL_FIELDS = [
   'label',
   'pluralLabel',
   'createLabel',
   'displayName',
   'displayNamePlural',
   'assessmentNavLabel',
   'studentSubjectsLabel',
];

/**
 * Returns a copy of a page module whose label fields are translated for the tenant.
 *
 * Both module configs are plain objects built at import time, i.e. before init.js has stored the
 * subdomain, so they cannot translate themselves. Pages resolve them at render instead:
 *
 *    const moduleConfig = withTenantLabels(props.moduleConfig || LESSON_MODULE);
 *
 * Anything that is not a label field is carried over untouched, and a CampusCloud tenant gets the
 * very same object back, so no page pays for this and no existing reference comparison changes.
 */
export const withTenantLabels = (module, subdomain) => {
   if (!module || typeof module !== 'object' || !isTvetTenant(subdomain)) {
      return module;
   }

   let changed = false;
   const translated = { ...module };

   MODULE_LABEL_FIELDS.forEach((field) => {
      const value = module[field];
      const next = translateLabelText(value, subdomain);

      if (next !== value) {
         translated[field] = next;
         changed = true;
      }
   });

   return changed ? translated : module;
};
